import { useTranslation } from 'react-i18next';
import { KoobBadge } from '@koob/ui';
import {
  actionLabel,
  bookedExperiences,
  bookingTrip,
  cancelledBookings,
  isFailedBooking,
  isStateChange,
  pricedName,
  pricingTarget,
  replacedBookings,
  resourceName,
  resourceTypeLabel,
  stateLabel,
  travelerNames,
  verbOf,
  verbTone,
} from './labels';

const OUTCOME_COLORS = { denied: 'orange', error: 'red' };
const TONES = {
  create: { icon: 'fa-plus', className: 'bg-green-50 text-green-600' },
  delete: { icon: 'fa-trash', className: 'bg-red-50 text-red-600' },
  access: {
    icon: 'fa-right-to-bracket',
    className: 'bg-gray-100 text-gray-500',
  },
  send: { icon: 'fa-paper-plane', className: 'bg-blue-50 text-blue-600' },
  edit: { icon: 'fa-pen', className: 'bg-blue-50 text-blue-600' },
  archive: { icon: 'fa-box-archive', className: 'bg-gray-100 text-gray-600' },
  unarchive: { icon: 'fa-box-open', className: 'bg-gray-100 text-gray-600' },
  failed: {
    icon: 'fa-triangle-exclamation',
    className: 'bg-orange-50 text-orange-600',
  },
  bookingFailed: {
    icon: 'fa-triangle-exclamation',
    className: 'bg-red-50 text-red-600',
  },
};

const toneOf = (row) => {
  if (isFailedBooking(row)) return 'bookingFailed';
  return row.outcome === 'success' ? verbTone(row) : 'failed';
};

/** Round icon of an audit row: the kind of action, or a warning when it failed. */
export function AuditLogIcon({ row, className = '' }) {
  const tone = TONES[toneOf(row)];
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${tone.className} ${className}`}
    >
      <i className={`fa-regular ${tone.icon} text-xs`} />
    </span>
  );
}

/**
 * "Updated User Jane Doe", with a Denied or Failed badge when it did not go
 * through. `name` replaces the resource name, a filter link for example.
 */
export function AuditLogSentence({ row, name }) {
  const { t } = useTranslation('auditLog');
  // KD-3937: a booking that did not go through, named in red.
  if (isFailedBooking(row) && !isStateChange(row)) {
    return (
      <span className="text-sm font-medium text-red-700">
        {resourceName(row)
          ? t('bookingFailed', { name: resourceName(row) })
          : t('bookingFailedUnnamed')}
      </span>
    );
  }
  if (pricingTarget(row)) {
    return (
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-900">
        <span className="font-medium">{actionLabel(t, row)}</span>
        {pricedName(row) && (
          <span className="font-medium">{pricedName(row)}</span>
        )}
      </span>
    );
  }
  // KD-3937: "Bookings cancelled (2) — Amber Kampot #12, …", not the folder.
  if (cancelledBookings(row).length) {
    return (
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-900">
        <span className="font-medium">
          {t('bookingsCancelled', { count: cancelledBookings(row).length })}
        </span>
        <span className="text-gray-500">—</span>
        <span className="font-medium">{cancelledBookings(row).join(', ')}</span>
      </span>
    );
  }
  // KD-3937: "Traveler Jane Doe added", not the folder they were added to.
  if (travelerNames(row).length) {
    return (
      <span className="text-sm font-medium text-gray-900">
        {t(`travelers.${verbOf(row)}`, {
          names: travelerNames(row).join(', '),
          count: travelerNames(row).length,
        })}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-900">
      {actionLabel(t, row) && (
        <span className="font-medium">{actionLabel(t, row)}</span>
      )}
      {replacedBookings(row).length > 0 && (
        <>
          <span className="text-gray-500">
            {resourceTypeLabel(t, 'bookings')}
          </span>
          <span className="font-medium">
            {replacedBookings(row).join(', ')}
          </span>
          <span className="text-gray-500">{t('replacedWith')}</span>
        </>
      )}
      {row.resourceType && (
        <span className="text-gray-500">
          {resourceTypeLabel(t, row.resourceType)}
        </span>
      )}
      {row.resourceId &&
        (name ?? <span className="font-medium">{resourceName(row)}</span>)}
      {!row.resourceId && bookedExperiences(row).length > 0 && (
        <span className="font-medium">{bookedExperiences(row).join(', ')}</span>
      )}
      {isStateChange(row) && (
        <span
          className={
            row.details?.to === 'failure' ? 'text-red-700' : 'text-gray-700'
          }
        >
          {row.details?.from
            ? t('stateTransition', {
                from: stateLabel(t, row.details.from),
                to: stateLabel(t, row.details.to),
              })
            : stateLabel(t, row.details?.to)}
        </span>
      )}
      {bookingTrip(row) && (
        <span className="text-gray-500">
          {t('fromTrip', { name: bookingTrip(row) })}
        </span>
      )}
      {row.outcome !== 'success' && (
        <KoobBadge size="sm" color={OUTCOME_COLORS[row.outcome] ?? 'gray'}>
          {t(`outcomes.${row.outcome}`)}
        </KoobBadge>
      )}
    </span>
  );
}

/** Why a booking did not go through, under its sentence (KD-3937). */
export function AuditLogFailureReason({ row }) {
  const { t } = useTranslation('auditLog');
  return isFailedBooking(row) ? (
    <span className="text-sm text-red-600">
      {t(`failureReasons.${row.details.failureReason}`)}
    </span>
  ) : null;
}

/**
 * Who did it: Koob itself or the supplier for a booking's state change (the
 * API names the hotel), else the user, an API key, Koob support or the other
 * organization.
 */
export function actorLabel(t, row) {
  if (row.authorKind === 'system') return t('system');
  if (row.authorKind === 'supplier') {
    return row.details?.author ?? t('supplier');
  }
  if (row.actor) return row.actor.name || row.actor.email;
  if (row.apiKey) return t('apiKey', { name: row.apiKey.name });
  if (row.koobSupport) return t('koobSupport');
  // Another organization's staff: the API names only it.
  return row.organization?.name ?? t('unknownUser');
}

export function ImpersonationBadge({ row }) {
  const { t } = useTranslation('auditLog');
  return row.impersonated ? (
    <KoobBadge size="sm" color="purple">
      {row.impersonator
        ? t('impersonatedBy', { name: row.impersonator.name })
        : t('koobSupport')}
    </KoobBadge>
  ) : null;
}

/** "MCP" when an AI assistant made the call through Koob's MCP server. */
export function ViaBadge({ row }) {
  const { t } = useTranslation('auditLog');
  return row.via ? (
    <KoobBadge size="sm" color="blue">
      {t(`via.${row.via}`)}
    </KoobBadge>
  ) : null;
}
